import axios from 'axios';
import auth from './auth';
import { toastError } from '../../Toaster/Toast';

/**
 * @typedef {Object} RequestOptions
 * @property {boolean} [authHeader=true] - Send Authorization header with token in request
 * @property {Object} [headers={}] - Other headers to include in the request
 * @property {boolean} [rawRequest=false] - Return the original response object instead of just the payload
 */

/**
 * Make an HTTP GET request with optional substitution
 * @param {string} endpoint - URL to send a GET request to
 * @param {Object} [params] - Key:Value pairs to substitute in the URL
 * @param {RequestOptions} [options] - Request options
 */
export const get = (
    endpoint,
    params = {},
    { authHeader = true, headers = {}, rawRequest = false, ...options } = {}
) => {
    if(authHeader) {
        const tokenExpiredError = auth.isTokenExpired();
        if (tokenExpiredError)
            return tokenExpiredError;
    }

    let target = endpoint.replace(/{([^}]+)}/g, (_, key) => params[key]);
    let config = { headers, ...options };

    if(authHeader) {
        config.headers.Authorization = `Bearer ${auth.getToken()}`;
    }

    return axios.get(target, config)
        .then(response => rawRequest ? response : response.data)
        .catch(ex => {
            const { data, status } = ex.response;
            
            if(status == 401) {
                auth.logout();
                toastError({ 
                    title: 'Session Expired', 
                    description: 'Your session has expired. Please log in again.' 
                });
                setTimeout(() => {
                    window.location.href = '/logout?reason=session-expired';
                }, 2000);
            }
            
            return {
                errored: true, 
                error: (ex.response ? data.error : ex.message) || ex
            };
        });
};

/**
 * Make an HTTP POST request
 * @param {string} endpoint - URL to send a POST request to
 * @param {Object} [data] - Body of the request
 * @param {RequestOptions} [options] - Request options
 */
export const post = (
    endpoint,
    data = {},
    { authHeader = true, headers = {}, rawRequest = false, ...options } = {}
) => {
    if(authHeader) {
        const tokenExpiredError = auth.isTokenExpired();
        if (tokenExpiredError)
            return tokenExpiredError;
    }

    let config = { headers, ...options };

    if(authHeader) {
        config.headers.Authorization = `Bearer ${auth.getToken()}`;
    }

    return axios.post(endpoint, data, config)
        .then(response => rawRequest ? response : response.data)
        .catch(ex => ({ errored: true, error: (ex.response ? ex.response.data.error : ex.message) || ex }));
};

/**
 * Make an HTTP DELETE request with optional substitution
 * @param {string} endpoint - URL to send a DELETE request to
 * @param {Object} [params] - Key:Value pairs to substitute in the URL
 * @param {RequestOptions} [options] - Request options
 */
export const remove = (
    endpoint,
    params = {},
    { authHeader = true, headers = {}, rawRequest = false, ...options } = {}
) => {
    if(authHeader) {
        const tokenExpiredError = auth.isTokenExpired();
        if (tokenExpiredError)
            return tokenExpiredError;
    }

    let target = endpoint.replace(/{(.*)}/g, (_, key) => params[key]);
    let config = { headers, ...options };

    if(authHeader) {
        config.headers.Authorization = `Bearer ${auth.getToken()}`;
    }

    return axios.delete(target, config)
        .then(response => rawRequest ? response : response.data)
        .catch(ex => ({ errored: true, error: (ex.response ? ex.response.data.error : ex.message) || ex }));
};

/**
 * Make an HTTP PUT request
 * @param {string} endpoint - URL to send a PUT request to
 * @param {Object} [data] - Body of the request
 * @param {RequestOptions} [options] - Request options
 */
export const put = (
    endpoint,
    data = {},
    { authHeader = true, headers = {}, rawRequest = false, ...options } = {}
) => {
    if(authHeader) {
        const tokenExpiredError = auth.isTokenExpired();
        if (tokenExpiredError)
            return tokenExpiredError;
    }

    let config = { headers, ...options };

    if(authHeader) {
        config.headers.Authorization = `Bearer ${auth.getToken()}`;
    }

    return axios.put(endpoint, data, config)
        .then(response => rawRequest ? response : response.data)
        .catch(ex => ({ errored: true, error: (ex.response ? ex.response.data.error : ex.message) || ex }));
};

/**
 * Make an HTTP PATCH request
 * @param {string} endpoint - URL to send a PATCH request to
 * @param {Object} [data] - Body of the request
 * @param {RequestOptions} [options] - Request options
 */
export const patch = (
    endpoint,
    data = {},
    { authHeader = true, headers = {}, rawRequest = false, ...options } = {}
) => {
    if(authHeader) {
        const tokenExpiredError = auth.isTokenExpired();
        if (tokenExpiredError)
            return tokenExpiredError;
    }

    let config = { headers, ...options };

    if(authHeader) {
        config.headers.Authorization = `Bearer ${auth.getToken()}`;
    }

    return axios.patch(endpoint, data, config)
        .then(response => rawRequest ? response : response.data)
        .catch(ex => ({ errored: true, error: (ex.response ? ex.response.data.error : ex.message) || ex }));
};

/**
 * These are kept for convenience purposes, future code should either directly import the request type it needs or import *.
 */
export default {
    get: (params, endpoint) => get(endpoint, params),
    post: (data, endpoint, options) => post(endpoint, data, options),
    delete: (params, endpoint) => remove(endpoint, params),
    put: (data, endpoint, options) => put(endpoint, data, options),
    patch: (data, endpoint, options) => patch(endpoint, data, options),
    putNoAuth: (data, endpoint, options) => put(endpoint, data, {...options, authHeader: false})
};
